import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    createModuleBatchRequest,
    loadModuleBatchesMetadata,
} from './api';


const MODULE_BATCH_STATUSES = ['МСК', 'ТМН', 'ХАБ'];

const fallbackMetadata = {
    location: { city: 'Тюмень', address: 'Локальный пример' },
    today: '2026-05-20',
    selected_status: 'МСК',
    statuses: MODULE_BATCH_STATUSES.map((status) => ({ value: status, label: status })),
    counts: { МСК: 0, ТМН: 0, ХАБ: 0 },
    modules: [],
    module_counts: {
        total: 0,
        by_system: { '5X': 0, '2X': 0, 'TE 2X': 0 },
    },
};

const initialForm = {
    whom: '',
    address: '',
    logisticsFile: null,
};


function formatDate(value) {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('ru-RU');
}


function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail || fallback;
}


function SummaryCard({ label, value }) {
    return (
        <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}


function ResultSection({ result }) {
    if (!result) {
        return <div className="shell-emptyState">Заявка логисту еще не создавалась.</div>;
    }

    return (
        <div className="shell-requestDetail">
            <div className="shell-requestDetailGrid">
                <div className="shell-requestDetailItem">
                    <span>Результат</span>
                    <strong>{result.detail}</strong>
                </div>
                <div className="shell-requestDetailItem">
                    <span>Заявка</span>
                    <strong>{result.request.application_name}</strong>
                </div>
                <div className="shell-requestDetailItem">
                    <span>Статус отправки</span>
                    <strong>{result.selected_status}</strong>
                </div>
                <div className="shell-requestDetailItem">
                    <span>Выбрано модулей</span>
                    <strong>{result.request.selected_count}</strong>
                </div>
                <div className="shell-requestDetailItem">
                    <span>Дата заявки</span>
                    <strong>{formatDate(result.request.application_sending)}</strong>
                </div>
                <div className="shell-requestDetailItem">
                    <span>Slug</span>
                    <strong>{result.request.slug}</strong>
                </div>
            </div>
        </div>
    );
}


export default function ModuleBatchesPage() {
    const navigate = useNavigate();
    const [selectedStatus, setSelectedStatus] = useState('МСК');
    const [reloadKey, setReloadKey] = useState(0);
    const [metadata, setMetadata] = useState(fallbackMetadata);
    const [warning, setWarning] = useState('');
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedSerials, setSelectedSerials] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [submitState, setSubmitState] = useState({
        loading: false,
        error: '',
    });
    const [result, setResult] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            setLoading(true);
            try {
                const payload = await loadModuleBatchesMetadata(selectedStatus);
                if (!cancelled) {
                    setMetadata(payload);
                    setWarning('');
                }
            } catch (_error) {
                if (!cancelled) {
                    setMetadata({
                        ...fallbackMetadata,
                        selected_status: selectedStatus,
                    });
                    setWarning('Не удалось загрузить список модулей. Показан локальный пример.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, [reloadKey, selectedStatus]);

    const filteredModules = useMemo(() => {
        const query = search.trim().toUpperCase();
        if (!query) {
            return metadata.modules || [];
        }

        return (metadata.modules || []).filter((module) => (
            module.serial_number?.includes(query)
            || module.party_number?.includes(query)
            || module.engineer?.toUpperCase().includes(query)
            || module.comment?.toUpperCase().includes(query)
        ));
    }, [metadata.modules, search]);

    const selectedModules = useMemo(
        () => (metadata.modules || []).filter((module) => selectedSerials.includes(module.serial_number)),
        [metadata.modules, selectedSerials],
    );

    const selectedCounters = useMemo(() => {
        const counters = { '5X': 0, '2X': 0, 'TE 2X': 0 };
        selectedModules.forEach((module) => {
            counters[module.system] = (counters[module.system] || 0) + 1;
        });
        return counters;
    }, [selectedModules]);

    function updateField(name, value) {
        setForm((current) => ({
            ...current,
            [name]: value,
        }));
    }

    function toggleSerial(serialNumber) {
        setSelectedSerials((current) => (
            current.includes(serialNumber)
                ? current.filter((item) => item !== serialNumber)
                : [serialNumber, ...current]
        ));
    }

    function openModuleDetails(module) {
        navigate(`/app/search/${module.id}`, {
            state: {
                backTo: '/app/module-batches',
                backLabel: 'Назад к отправке',
            },
        });
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setSubmitState({ loading: true, error: '' });

        const formData = new FormData();
        formData.append('status', selectedStatus);
        formData.append('whom', form.whom);
        formData.append('address', form.address);
        formData.append('serial_numbers', selectedSerials.join(','));
        if (form.logisticsFile) {
            formData.append('logistics_file', form.logisticsFile);
        }

        try {
            const response = await createModuleBatchRequest(formData);
            setResult(response);
            setSelectedSerials([]);
            setForm(initialForm);
            setSearch('');
            setSubmitState({ loading: false, error: '' });
            setReloadKey((current) => current + 1);
        } catch (error) {
            setSubmitState({
                loading: false,
                error: extractErrorMessage(error, 'Не удалось создать заявку логисту.'),
            });
        }
    }

    const summaryCards = [
        { label: 'Локация', value: metadata.location?.city || '—' },
        { label: `Доступно ${selectedStatus}`, value: loading ? '...' : (metadata.counts?.[selectedStatus] ?? 0) },
        { label: 'Выбрано модулей', value: selectedSerials.length },
        { label: '5X / 2X / TE', value: `${selectedCounters['5X']} / ${selectedCounters['2X']} / ${selectedCounters['TE 2X']}` },
    ];

    return (
        <div className="shell-page">
            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact">
                    <div className="shell-pageBandIntro">
                        <h3>Отправка МСК / ТМН / ХАБ</h3>
                    </div>

                    <div className="shell-pageSummary shell-pageSummary--queueTop shell-pageSummary--header">
                        {summaryCards.map((card) => (
                            <SummaryCard key={card.label} label={card.label} value={card.value} />
                        ))}
                    </div>
                </div>
            </section>

            {warning ? <div className="shell-banner">{warning}</div> : null}

            <section className="shell-section">
                <div className="shell-tabRow">
                    {MODULE_BATCH_STATUSES.map((status) => (
                        <button
                            key={status}
                            type="button"
                            className={`shell-tabButton${selectedStatus === status ? ' is-active' : ''}`}
                            onClick={() => {
                                setSelectedStatus(status);
                                setSelectedSerials([]);
                                setResult(null);
                            }}
                        >
                            {status}
                            {' · '}
                            {metadata.counts?.[status] ?? 0}
                        </button>
                    ))}
                </div>

                <div className="shell-toolbarRow">
                    <label className="shell-field shell-field--toolbar">
                        <span>Фильтр</span>
                        <input
                            type="text"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Серийный номер, партия, инженер, комментарий"
                        />
                    </label>
                </div>

                <div className="shell-sectionHeader shell-sectionHeader--nested">
                    <div>
                        <h3>Свободные модули</h3>
                    </div>
                    <span>{loading ? 'Загрузка...' : `${filteredModules.length} шт.`}</span>
                </div>

                {loading ? (
                    <div className="shell-emptyState">Загружаем модули...</div>
                ) : filteredModules.length ? (
                    <div className="shell-tableWrap">
                        <table className="shell-table">
                            <thead>
                                <tr>
                                    <th>Выбор</th>
                                    <th>Серийный номер</th>
                                    <th>Партия / система</th>
                                    <th>Статус ремонта</th>
                                    <th>Инженер</th>
                                    <th>Локация</th>
                                    <th>Комментарий</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredModules.map((module) => {
                                    const checked = selectedSerials.includes(module.serial_number);
                                    return (
                                        <tr key={module.id} className="shell-tableRow">
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="shell-tableCheckbox"
                                                    checked={checked}
                                                    onChange={() => toggleSerial(module.serial_number)}
                                                />
                                            </td>
                                            <td><strong>{module.serial_number}</strong></td>
                                            <td>
                                                {module.party_number || '—'}
                                                <div className="shell-tableHint">{module.system || '—'}</div>
                                            </td>
                                            <td>{module.repair_status || '—'}</td>
                                            <td>{module.engineer || '—'}</td>
                                            <td>{module.location?.city || '—'}</td>
                                            <td>{module.comment || '—'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                                                    onClick={() => openModuleDetails(module)}
                                                >
                                                    Детали
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="shell-emptyState">Для выбранного статуса свободных модулей нет.</div>
                )}

                <div className="shell-grid shell-grid--main">
                    <div className="shell-section shell-section--embedded">
                        <div className="shell-sectionHeader shell-sectionHeader--nested">
                            <div>
                                <h3>Заявка логисту</h3>
                            </div>
                        </div>

                        <form className="shell-searchForm shell-searchForm--compact" onSubmit={handleSubmit}>
                            <label className="shell-field">
                                <span>Кому</span>
                                <input
                                    type="text"
                                    value={form.whom}
                                    onChange={(event) => updateField('whom', event.target.value)}
                                    placeholder="Например: СЦ Москва"
                                />
                            </label>

                            <label className="shell-field">
                                <span>Адрес</span>
                                <input
                                    type="text"
                                    value={form.address}
                                    onChange={(event) => updateField('address', event.target.value)}
                                    placeholder="Адрес получателя"
                                />
                            </label>

                            <label className="shell-field">
                                <span>Акт приёма-передачи</span>
                                <input
                                    type="file"
                                    accept=".xls,.xlsx"
                                    onChange={(event) => updateField('logisticsFile', event.target.files?.[0] || null)}
                                />
                            </label>

                            <div className="shell-searchActions">
                                <button
                                    type="submit"
                                    className="shell-actionButton shell-actionButton--primary"
                                    disabled={submitState.loading}
                                >
                                    {submitState.loading ? 'Создаем...' : 'Создать заявку'}
                                </button>
                            </div>
                        </form>

                        {submitState.error ? (
                            <div className="shell-inlineMessage shell-inlineMessage--error">
                                {submitState.error}
                            </div>
                        ) : null}
                    </div>

                    <div className="shell-inlineStack">
                        <div className="shell-resultCard shell-resultCard--summary">
                            <div className="shell-sectionHeader shell-sectionHeader--nested">
                                <div>
                                    <h3>Последняя операция</h3>
                                </div>
                            </div>
                            <ResultSection result={result} />
                        </div>

                        <div className="shell-section shell-section--embedded">
                            <div className="shell-sectionHeader shell-sectionHeader--nested">
                                <div>
                                    <h3>Выбранные модули</h3>
                                </div>
                                <span>{selectedSerials.length} шт.</span>
                            </div>

                            {selectedModules.length ? (
                                <div className="shell-tableWrap">
                                    <table className="shell-table">
                                        <thead>
                                            <tr>
                                                <th>Серийный номер</th>
                                                <th>Партия / система</th>
                                                <th>Инженер</th>
                                                <th>Локация</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedModules.map((module) => (
                                                <tr key={module.id} className="shell-tableRow">
                                                    <td><strong>{module.serial_number}</strong></td>
                                                    <td>
                                                        {module.party_number || '—'}
                                                        <div className="shell-tableHint">{module.system || '—'}</div>
                                                    </td>
                                                    <td>{module.engineer || '—'}</td>
                                                    <td>{module.location?.city || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="shell-emptyState">Выбери модули из таблицы выше.</div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
